import DateFormat from '../../../utils/dateFormat';
import DateItem from './DateItem';
import IconButton from 'src/components/Buttons/IconButton';
import moment from '../../../utils/moment';
import months from '../../../constants/months';
import Popover from '@mui/material/Popover';
import React, { useMemo } from 'react';
import SVG from '../../../components/Images/SvgRenderer';
import useStyles from './style';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getUserSetting } from 'src/utils/useUser';
import { isCypress } from '../../../utils/useCypress';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { moveLastToFirstInArray } from 'src/utils/useFunctions';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useAppSelector } from '../../../hooks/redux-hooks';
import { useEffect } from 'src/utils/useEffect';
import { useStates } from 'src/utils/useState';
import { useTranslation } from 'react-i18next';
import { weekdaysMin } from '../../../constants/weekdays';

type DatePickerType = {
  label?: any;
  presetDate?: any;
  setDate?: any;
  presetDateRange?: any;
  time?: any;
  weekNumber?: any;
  start?: any;
  end?: any;
  disabled?: any;
  dataCy?: any;
};

const DatePicker: React.FunctionComponent<DatePickerType> = ({ label, presetDate = moment(), setDate, presetDateRange, time = true, weekNumber, start, end, disabled = false, dataCy }) => {
  
  let dateRange: any = {};
  if(presetDateRange.start == null) dateRange.start = moment();
  else dateRange.start = presetDateRange.start;
  if(presetDateRange.end == null) dateRange.end = moment();
  else dateRange.end = presetDateRange.end;
  const { t } = useTranslation();
  const languageData = useAppSelector((state: any) => state.language);
  const userData = useAppSelector((state: any) => state.user);

  const [state, setState] = useStates({
    currentYearMonth: moment((presetDate && presetDate !== null) ? presetDate : undefined),
    anchorEl: null,
    hours: moment(presetDate).format("HH"),
    minutes: moment(presetDate).format("mm"),
  });

  useEffect(() => {
    setState("currentYearMonth", moment((presetDate && presetDate !== null) ? presetDate : undefined));
  }, [presetDate, state.anchorEl, setState], [presetDate, state.anchorEl]);

  const classes = useStyles({ weekNumber });

  const firstDayInWeek = getUserSetting(userData.userSettings, "customizations", ["calendar", "first_day_in_week"]);
  const weekdays: any = firstDayInWeek === 0 ? moveLastToFirstInArray(weekdaysMin) : weekdaysMin;

  const firstDate = useMemo(() => {
    const startOfMonth = new Date(state.currentYearMonth.year(), state.currentYearMonth.month(), 0);
    return moment(startOfMonth).subtract(startOfMonth.getDay(), 'days');
  }, [state.currentYearMonth]);

  const handleClick = (e: any) => {
    if(!disabled) {
      setState("anchorEl", e.currentTarget);
    }
  };

  const handleClose = () => {
    if(!disabled) {
      setState("anchorEl", null);
    }
  };

  const handleNextMonth = (e: any) => {
    e.stopPropagation();
    setState("currentYearMonth", moment(state.currentYearMonth).add(1, 'months'));
  };

  const handleLastMonth = (e: any) => {
    e.stopPropagation();
    setState("currentYearMonth", moment(state.currentYearMonth).subtract(1, 'months'));
  };

  const updateDate = (momentDate: any) => {
    let tempDate = momentDate;
    let tempHours: any = state.hours;
    let tempMinutes: any = state.minutes;
    if(tempHours === "Invalid Date") tempHours = "12";
    if(tempMinutes === "Invalid Date") tempMinutes = "00";
    tempDate = moment(tempDate).set('hour', tempHours);
    tempDate = moment(tempDate).set('minute', tempMinutes);
    setDate(tempDate);
  }; 

  const handleOnChangeTime = (value: any) => {
    setDate(moment(value));
  };

  useEffect(() => {
    if(end) {
      if(presetDateRange.start !== null && moment((presetDate && presetDate !== null) ? presetDate : undefined).isBefore(moment(dateRange.start))) {
        if(time) {
          setDate(moment(dateRange.start).add(30, 'minutes'));
          setState("currentYearMonth", moment(dateRange.start).add(30, 'minutes'));
        } else {
          setDate(moment(dateRange.start));
          setState("currentYearMonth", moment(dateRange.start));
        }
      }
    }  
  }, [dateRange.start, end, presetDate, presetDateRange.start, setDate, setState], [dateRange.start]);

  const customClockIcon = () => (
    <SVG src="clock"/>
  );

  const slots = {
    openPickerIcon: customClockIcon,
  };
  
  const slotProps = {
    desktopPaper: {
      className: classes.timepickerPaper
    },
    digitalClockSectionItem: {
      className: classes.timepickerItem,
    },
    openPickerButton: {
      className: classes.timepickerClockButton,
    },
    actionBar: {
      actions: [],
    },
  };

  return (
    <>
      <div className={classes.wrapper}>
        {
          label ? (
            <label>
              {label}
            </label>
          ) : null
        }
        <div className={classes.inputs}>
          <div className={`${classes.datePickerInput} ${disabled ? 'disabled' : null}`} onClick={handleClick} data-cy={isCypress() ? dataCy : null}>
            <p className={classes.selectedDate} data-clarity-unmask="true">
              {
                presetDate === null ? (
                  <span style={{color: "#787878"}}>{t('select_date')}</span>
                ) : (
                  <>{DateFormat(moment((presetDate && presetDate !== null) ? presetDate : undefined).toString(), "default", languageData, t)}</>)
                }
            </p>
          </div>
          {
            time ? (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TimePicker className={classes.timePickerInput} value={presetDate} ampm={false} onChange={handleOnChangeTime} slots={slots} slotProps={slotProps}/>
              </LocalizationProvider>
            ) : null
          }
        </div>
      </div>
      <Popover className={classes.calendarWrapper} open={Boolean(state.anchorEl)} anchorEl={state.anchorEl} onClose={handleClose} anchorOrigin={{vertical: 'bottom', horizontal: 'right'}} transformOrigin={{vertical: 'top', horizontal: 'right'}}>
        <div className={classes.calendar}>
          <div className={classes.calendarHeader}>
            <p data-clarity-unmask="true">{`${t(months[state.currentYearMonth.month()].toLowerCase())} ${state.currentYearMonth.year()}`}</p>
            <div className={classes.navigator}>
              <IconButton onClick={handleLastMonth} dataCy='prevMonthButton'>
                <SVG src="chevron-left"/>
              </IconButton>
              <IconButton onClick={handleNextMonth} dataCy='nextMonthButton'>
                <SVG src="chevron-right"/>
              </IconButton>
            </div>
          </div>
          <div className={classes.weekdayHeader}>
            {
              weekNumber ? (
                <div className={classes.weekday}>
                  <p>{t("week").substring(0,1)}</p>
                </div>
              ) : null
            }
            {
              weekdays.map((weekday: any, key: any) => (
                <div className={classes.weekday} key={`k_${key}`}>
                  <p data-clarity-unmask="true">{t(weekday.toLowerCase())}</p>
                </div>
              ))
            }
          </div>
          <div className={classes.dateWrapper}>
            {
              new Array(weekNumber ? 48 : 42).fill('').map((_, key) => {
                const itemDate = weekNumber ? moment(firstDate).add(key % 8 === 0 ? key - Math.floor(key / 8) : key - Math.ceil(key / 8), 'days').add(firstDayInWeek, 'day') : moment(firstDate).add(key, 'days').add(firstDayInWeek, 'day');
                return (
                  <DateItem
                    index={key}
                    key={`k_${key}`}
                    date={itemDate}
                    start={start}
                    end={end}
                    setDate={updateDate}
                    currentSelectedDate={(presetDate && presetDate !== null) ? presetDate : undefined}
                    dateRange={dateRange}
                    presetDateRange={presetDateRange}
                    weekNumberMode={weekNumber}
                    handleClose={handleClose}
                  />
                )
              })
            }
          </div>
        </div>
      </Popover>
    </>
  );
};

export default DatePicker;